import { UI } from '../nextpage';
import { NavTypes } from '../utilities/enums';
import { NavigationItem } from '../interfaces/inavigation-item';

export class NavBuilder {
    constructor(private NP: UI) {
        // Navigation builder
    }

    // Find the start URL provided in the navigation map provided
    findNavStart(navStructure: Array<NavigationItem>, startPage: string): NavigationItem {
        if (startPage === undefined || startPage === null) {
            return null;
        }
        let startFound = null;
        navStructure.forEach((navObj: NavigationItem) => {
            if (!navObj.hasOwnProperty("url") || !navObj.hasOwnProperty("title")) {
                return;
            }
            if (navObj.url === startPage.substr(1, startPage.length - 2)) {
                startFound = navObj;
                return;
            }
            if (!navObj.hasOwnProperty("children") || navObj.children.length === 0) {
                return;
            }
            const nextStart = this.findNavStart(navObj.children, startPage);
            if (nextStart === null) {
                return;
            }
            startFound = nextStart;
        });
        return startFound;
    }

    // Get a topnav element structure
    getTopNavStructure(navStructure: Array<NavigationItem>, navFragment: DocumentFragment, level: number, parentUrl: string, parentTitle: string): void {
        const navElement = document.createElement("nav");
        this.NP.toggleAriaState(navElement, "hidden", true);
        navElement.classList.add(level === 0 ? "mainnav" : "subnav");
        navElement.setAttribute("aria-label", parentTitle);

        navFragment.append(navElement);

        const navList = document.createElement("ul");
        if (level > 0) {
            navElement.dataset.navParent = parentUrl;

            const prevMenu = document.createElement("li");
            prevMenu.classList.add("previous");
            prevMenu.innerHTML = "previous menu";

            const parentLink = document.createElement("li");
            parentLink.classList.add("parent-link");

            const parentAnchor = document.createElement("a");
            parentAnchor.href = parentUrl;
            parentAnchor.innerHTML = parentTitle;

            parentLink.append(parentAnchor);
            navList.append(prevMenu, parentLink);
        }

        navStructure.forEach((navObj: NavigationItem) => {
            if (!navObj.hasOwnProperty("url") || !navObj.hasOwnProperty("title")) {
                return;
            }
            
            const listElement = document.createElement("li");
            if (!navObj.hasOwnProperty("children") || navObj.children.length <= 0) {
                const listAnchor = document.createElement("a");
                listAnchor.href = navObj.url;
                listAnchor.innerHTML = navObj.title;

                listElement.append(listAnchor);
                navList.append(listElement);
                return;
            }

            listElement.dataset.subnav = navObj.url;
            listElement.innerHTML = navObj.title;
            this.getTopNavStructure(navObj.children, navFragment, level + 1, navObj.url, navObj.title);

            navList.append(listElement);
        });

        navElement.append(navList);
    }

    // Dynamically build a mega-menu structure from a mapping (not used when the `megaMenuType` is `custom`)
    getMegaMenuStructure(navStructure: Array<NavigationItem>, navFragment: DocumentFragment | HTMLElement, columnSize: number): void {
        const mmHeadings = document.createElement("div");
        mmHeadings.classList.add("row");

        const mmContainer = document.createElement("div");
        mmContainer.classList.add("row");

        navStructure.forEach((navObj: NavigationItem) => {
            const mmHeadContainer = document.createElement("div");
            mmHeadContainer.classList.add("col-" + columnSize);

            const navAnchor = document.createElement("a");
            navAnchor.href = navObj.url;

            const navHeading = document.createElement("h3");
            navHeading.innerHTML = navObj.title;

            navAnchor.append(navHeading);
            mmHeadContainer.append(navAnchor);
            mmHeadings.append(mmHeadContainer);

            if (!navObj.hasOwnProperty("children") || navObj.children.length <= 0) {
                return;
            }

            const mmColContainer = document.createElement("div");
            mmColContainer.classList.add("col-" + columnSize);
            this.getSpecificNavStructure(navObj.children, mmColContainer, 0, true);

            mmContainer.append(mmColContainer);
        });
        navFragment.append(mmHeadings, mmContainer);
    }

    // Builds a site-specific navigation menu structure
    getSpecificNavStructure(navStructure: Array<NavigationItem>, navFragment: DocumentFragment | HTMLElement, level: number, inMegaMenu: boolean): void {
        if (level > 1) {
            return;
        }
        const navList = document.createElement("ul");
        if (inMegaMenu === true) {
            navList.classList.add("in-mega", "arrows");
        }
        navStructure.forEach((navObj: NavigationItem) => {
            if (!navObj.hasOwnProperty("url") || !navObj.hasOwnProperty("title")) {
                return;
            }

            const navItem = document.createElement("li");
            const navAnchor = document.createElement("a");
            navAnchor.href = navObj.url;
            navAnchor.innerHTML = navObj.title;
            navItem.append(navAnchor);

            if (inMegaMenu === true || !navObj.hasOwnProperty("children") || navObj.children.length <= 0) {
                navList.append(navItem);
                return;
            }

            if (!navObj.hasOwnProperty("megaMenu")) {
                this.getSpecificNavStructure(navObj.children, navItem, level + 1, false);
            } else {
                const megaMenuContainer = document.createElement("div");
                megaMenuContainer.classList.add("mega-menu");
                if (navObj.megaMenu.hasOwnProperty("customClasses")) {
                    megaMenuContainer.classList.add(...(typeof(navObj.megaMenu.customClasses) === "string" ? [navObj.megaMenu.customClasses] : navObj.megaMenu.customClasses));
                }
                if (navObj.megaMenu.hasOwnProperty("customContent")) {
                    if (typeof(navObj.megaMenu.customContent) === "string") {
                        megaMenuContainer.innerHTML = navObj.megaMenu.customContent;
                    } else if (Array.isArray(navObj.megaMenu.customContent)) {
                            megaMenuContainer.append(...navObj.megaMenu.customContent);
                    } else {
                        megaMenuContainer.append(navObj.megaMenu.customContent);
                    }
                } else {
                    this.getMegaMenuStructure(navObj.children, megaMenuContainer, navObj.megaMenu.columnSize);
                }
                if (megaMenuContainer.children.length > 0) {
                    navItem.append(megaMenuContainer);
                }
            }
            navList.append(navItem);
        });

        navFragment.append(navList);
    }

    // Builds a detail page navigation structure
    getLeftNavStructure(navStructure: Array<NavigationItem>, navFragment: DocumentFragment | HTMLElement, startPage: string): void {
        function getNavList(navStructure: Array<NavigationItem>, navFragment: DocumentFragment | HTMLElement, level: number): void {
            const maxLevels = 2;
            if (level >= maxLevels) {
                return;
            }
            const navList = document.createElement("ul");
            navStructure.forEach((navObj: NavigationItem) => {
                if (!navObj.hasOwnProperty("url") || !navObj.hasOwnProperty("title")) {
                    return;
                }
                const hasChildren = navObj.hasOwnProperty("children") && navObj.children.length > 0;
                const navItem = document.createElement("li");
                if (navObj.url.toLowerCase() === window.location.pathname.toLowerCase()) {
                    this.NP.toggleAriaState(navItem, "selected", true);
                }
                const navAnchor = document.createElement("a");
                navAnchor.href = navObj.url;
                navAnchor.innerHTML = navObj.title;

                if (!hasChildren) {
                    navItem.append(navAnchor);
                    navList.append(navItem);
                    return;
                }

                navItem.classList.add("children");
                const navToggle = document.createElement("span");
                navToggle.classList.add("nav-toggle");

                navItem.append(navToggle);
                navItem.append(navAnchor);

                getNavList(navObj.children, navItem, level + 1);
                navList.append(navItem);
            });
            navFragment.append(navList);
        }
        const navStart = this.findNavStart(navStructure, startPage);
        if (navStart === null) {
            return;
        }
        const navContainer = document.createElement("div");
        navContainer.classList.add("col");

        const navHeading = document.createElement("h4");
        const navHeadingAnchor = document.createElement("a");
        navHeadingAnchor.href = navStart.url;
        navHeadingAnchor.innerHTML = navStart.title;
        navHeading.append(navHeadingAnchor);
        navContainer.append(navHeading);

        if (!navStart.hasOwnProperty("children") || navStart.children.length === 0) {
            navFragment.append(navContainer);
            return;
        }

        getNavList(navStart.children, navContainer, 0);
        navFragment.append(navContainer);
    }

    getNavContainer(navType: NavTypes): Element | null {
        if (this.NP.options.navigationItems.length < 3 && this.NP.options.navigationRoutes.length < 3) {
            console.log("You must have at least three nav objects in the `NextPage.options.navigationItems` or `NextPage.options.navigationRoutes` array.")
            return null;
        }
        switch(navType) {
            case NavTypes.TOPMENU:
                return document.querySelector("#nav-container");
            case NavTypes.SITESPECIFIC:
                return document.querySelector("#site-specific-nav");
            case NavTypes.DETAIL:
                return document.querySelector("#left-nav");
            default:
                console.log("An invalid navigation type was provided. Use the `NextPage.navigationTypes` enum to see valid navigation types.");
                return null;
        }
    }

    buildNavigation(navType: NavTypes): void {
        const navContainer = this.getNavContainer(navType) as HTMLElement;
        if (navContainer === null || this.NP.options.navigationItems.length < 3) {
            return;
        }
        const navFragment = document.createDocumentFragment();
        switch(navType) {
            case NavTypes.TOPMENU:
                const sitenameAnchor = document.querySelector("#sitename a:not(#hallmark)");
                // We use .textContent instead of .innerText to prevent an unnecessary re-flow of the DOM below
                this.getTopNavStructure(this.NP.options.navigationItems, navFragment, 0, null, sitenameAnchor.textContent);
                break;
            case NavTypes.SITESPECIFIC:
                this.getSpecificNavStructure(this.NP.options.navigationItems, navFragment, 0, false);
                break;
            case NavTypes.DETAIL:
                this.getLeftNavStructure(this.NP.options.navigationItems, navFragment, navContainer.dataset.startUrl);
                break;
            default:
                return;
        }
        navContainer.append(navFragment);
    }
}